import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import useFunderDetails, { TParams } from "../../custom-hooks/useFunderDetails";
import ButtonLoader, { Loader } from "../../UI/Loaders/Loaders";
import CustomSelectDropdown from "../CustomHTMLElements/CustomSelectDropdown";
import Paginator from "../Pagination/Pagination.component";
import { showModal } from "../../redux/actions/modal";
import { ROLE_TYPES } from "../../helpers/roleTypes";
import { appInsights } from "../../config/appInsights";
import { getBlobData } from "../../newApis/dotNetApiMethods";
import * as ajaxEndpoints from "../../api/ajax-endpoints";
import { RootState } from "../../redux/store";
import { setAdvancedInvestorSearchParams } from "../../redux/actions/investors";

const Investor = () => {
  const [preState, setPreState] = useState<TParams>({
    pageNumber: 1,
    size: 20,
    searchBy: 3,
  });
  const [state, setState] = useState<TParams>({
    pageNumber: 1,
    size: 20,
    searchBy: 3,
    startDate: "",
    endDate: "",
  });

  const [isDownloading, setIsDownloading] = useState(false);

  const dispatch = useDispatch();
  const loggedInUser = useSelector(
    (state: RootState) => state?.authReducer?.loggedInUser,
  );

  const searchParams = useSelector(
    (state: RootState) => state?.investorSearchReducer,
  );

  useEffect(() => {
    appInsights.trackPageView({
      name: "Investors  - (Investor.jsx)",
      isLoggedIn: true,
    });
  }, []);

  const { data, status, error } = useFunderDetails({
    ...preState,
    searchValue: searchParams?.searchValue,
    searchBy: searchParams?.searchBy,
    aggregatorId: searchParams?.aggregatorId,
    email: searchParams?.email,
  });

  const resetTable = () => {
    setPreState({ ...state, pageNumber: 1 });
  };
  const modalHandler = (modalData: any, type: string) => {
    dispatch(
      showModal({
        modalIdentifier: "audittrail",
        dataRecord: modalData,
        action: "customrange",
        type,
      }),
    );
  };
  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setState({ ...state, [name]: value });
  };

  const filterData = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const diffTime =
      new Date(state.endDate!).valueOf() - new Date(state.startDate!).valueOf();
    if (diffTime < 0) {
      modalHandler("", "alert");
      return;
    } else {
      setPreState(state);
    }
    resetTable();
  };

  const onClear = () => {
    setState({
      pageNumber: 1,
      size: 20,
      searchBy: 3,
      startDate: "",
      endDate: "",
    });
    setPreState({
      pageNumber: 1,
      size: 20,
      searchBy: 3,
    });
    dispatch(
      setAdvancedInvestorSearchParams({
        searchValue: "",
        searchBy: 3,
        aggregatorId: "",
        email: "",
      }),
    );
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      let queryParams = `?Page=${preState?.pageNumber}&PageSize=${preState?.size}`;
      if (searchParams?.searchBy) {
        queryParams = `${queryParams}&FunderType=${searchParams?.searchBy}`;
      }
      if (preState?.startDate) {
        queryParams = `${queryParams}&from_date=${preState?.startDate}`;
      }
      if (preState?.endDate) {
        queryParams = `${queryParams}&to_date=${preState?.endDate}`;
      }
      if (searchParams?.searchValue) {
        queryParams = `${queryParams}&FunderName=${searchParams?.searchValue}`;
      }
      if (preState?.FunderId) {
        queryParams = `${queryParams}&FunderId=${preState?.FunderId}`;
      }
      if (searchParams?.aggregatorId) {
        queryParams = `${queryParams}&AggregatorId=${searchParams?.aggregatorId}`;
      }
      if (searchParams?.email) {
        queryParams = `${queryParams}&Email=${searchParams?.email}`;
      }
      const response = await getBlobData(
        `${ajaxEndpoints.DOWLOAD_INVESTORS}${queryParams}`,
      );
      if (response?.status === 200) {
        const url = window.URL.createObjectURL(new Blob([response?.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `${
            preState.startDate && preState.endDate
              ? `Investor list from ${preState.startDate} to ${preState.endDate}.csv`
              : `investor_list.csv`
          }`,
        );
        link.click();
      }
    } catch (error) {}
    setIsDownloading(false);
  };

  let RenderedComponent = null;
  if (!data && status !== "loading") {
    RenderedComponent = (
      <div>
        <div className="grid__padding animated fadeInRight">
          <h4 className="center-txt">
            <i className="fas fa-bell" /> Unable to Fetch Investors
          </h4>
        </div>
      </div>
    );
  } else if (status === "loading") {
    RenderedComponent = <Loader centered={true} text="Loading investors..." />;
  } else {
    if (error) {
      RenderedComponent = (
        <div>
          <div className="grid__padding animated fadeInRight">
            <h4 className="center-txt p-y-5">
              <i className="fas fa-bell" /> There was an issue while fetching
              investors, Please Reload The Page{" "}
            </h4>
          </div>
        </div>
      );
    } else if (
      (data && data.data && data.data.length === 0) ||
      (data && !data.data)
    ) {
      RenderedComponent = (
        <div className="centerLoader">
          <div className="grid__padding animated fadeInRight">
            <h4 className="center-txt p-y-5">
              <i className="fas fa-bell" /> There are currently no investor.
            </h4>
          </div>
        </div>
      );
    } else {
      RenderedComponent = (
        <div className="row m-x-sm m-y-2 grid__padding">
          <div className="table-responsive">
            <table className="table">
              <thead className="bg-lighter-grey">
                <tr>
                  <th>S/N</th>
                  <th>Business Name</th>
                  <th>Name</th>
                  <th>Aggregator</th>
                  <th>Investor Type</th>
                  <th>Contact Email</th>
                  <th>Gender</th>
                  <th>KYC Scores</th>
                  <th>Country</th>
                  <th>Created Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.data &&
                  data.data.map((request: any, _id: number) => {
                    const {
                      email,
                      funder_id,
                      first_name,
                      last_name,
                      funder_type,
                      pub_date,
                      gender,
                      country,
                      biz_name,
                      aggregator_name,
                      kyc_score,
                    } = request;
                    return (
                      <tr key={_id}>
                        <td>
                          {(preState.pageNumber - 1) * preState.size +
                            (_id + 1)}
                        </td>
                        <td>{biz_name || "N/A"}</td>
                        <td>
                          {first_name} {last_name}
                        </td>
                        <td>{aggregator_name || "N/A"}</td>
                        <td>{funder_type}</td>
                        <td>{email}</td>
                        <td>{gender}</td>
                        <td>{kyc_score}</td>
                        <td>{country}</td>
                        <td>{pub_date?.substring(0, 10)}</td>
                        <td>
                          <Link
                            to={`/investor/${funder_id}`}
                            className="color-blue"
                          >
                            View Details
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      );
    }
  }
  return (
    <div>
      <div className="datatable-wrapper">
        <div className="table-view speed-1x animated fadeInRight">
          <div className="d-flex flex-row flex-wrap mb-4">
            <h3 className="page-title">Investors</h3>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "1.5rem",
                width: "30rem",
              }}
            >
              <button
                type="button"
                className="btn advancly-btn m-0"
                onClick={handleDownload}
                disabled={status === "loading" || isDownloading}
              >
                <i className="fas fa-download mr-1" />
                Download {isDownloading && <ButtonLoader />}
              </button>

              {loggedInUser?.roles.includes(
                ROLE_TYPES.CUSTOMER_CREATION_INITIATOR,
              ) && (
                <button
                  type="button"
                  className="btn advancly-white-btn btn-md m-0"
                  onClick={() =>
                    dispatch(
                      showModal({
                        modalIdentifier: "investors",
                        action: "signupinvestor",
                      }),
                    )
                  }
                >
                  Create Individual Investor
                </button>
              )}
            </div>
          </div>
          <div className="row">
            <div className="form-group col-md-2">
              <label>From</label>
              <div className="input-group">
                <input
                  type="date"
                  name="startDate"
                  className="form-control"
                  value={state.startDate}
                  onChange={(e) => handleChange(e)}
                />
              </div>
            </div>
            <div className="form-group col-md-2">
              <label>To</label>
              <div className="input-group">
                <input
                  type="date"
                  className="form-control"
                  name="endDate"
                  value={state.endDate}
                  onChange={(e) => handleChange(e)}
                />
              </div>
            </div>
            <div className="form-group col-md-2" style={{ marginTop: "30px" }}>
              <div className="input-group">
                <CustomSelectDropdown
                  name="size"
                  onChange={(e) => handleChange(e)}
                  value={state.size}
                  isShowDropdownArrow
                >
                  <option value={10}>10 Entries</option>
                  <option value={20}>20 Entries</option>
                  <option value={50}>50 Entries</option>
                  <option value={100}>100 Entries</option>
                </CustomSelectDropdown>
              </div>
            </div>
            <div className="form-group col-md-2" style={{ marginTop: "30px" }}>
              <div className="input-group">
                <button
                  className="btn advancly-white-btn ml-2 form-control"
                  onClick={() => {
                    dispatch(
                      showModal({
                        modalIdentifier: "advancedinvestorsearch",
                        dataRecord: {},
                      }),
                    );
                  }}
                >
                  <i className="fas fa-search" />
                  &nbsp; Advance Search
                </button>
              </div>
            </div>
            <div className="form-group col-md-2" style={{ marginTop: "30px" }}>
              <div className="input-group">
                <button
                  className="btn advancly-btn form-control"
                  onClick={(e: any) => filterData(e)}
                  disabled={status === "loading" || isDownloading}
                >
                  Search
                </button>
              </div>
            </div>
            <div className="form-group col-md-2" style={{ marginTop: "30px" }}>
              <div className="input-group">
                <button
                  type="button"
                  className="btn advancly-red-btn btn-md"
                  style={{ height: "3rem" }}
                  onClick={onClear}
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
          {/*Datatable || Loader || Error Message*/}
          {RenderedComponent}
          {data && data?.data && (
            <Paginator
              size={preState.size}
              page={preState.pageNumber}
              count={data?.totalCount || 0}
              changeCurrentPage={(pageNumber: number) =>
                setPreState({ ...state, pageNumber })
              }
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Investor;
